import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  Box,
  Stack,
  Tooltip,
  Button,
  ToggleButton,
  ToggleButtonGroup
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import VerifiedIcon from "@mui/icons-material/Verified";
import WarningIcon from "@mui/icons-material/Warning";
import { useSelector } from "react-redux";
import { Postrequestcall } from "../../request/Postrequest";
import { FETCH_COMPANIES_BY_NAME_NEW, SAVE_COMPANIES } from "../../constant/Apipath";
import UseDebounce from "./UseDebounce";
import Removeduplicates from "./Removeduplicate";

const PAGE_SIZE = 20;

export default function CompanySelectModal({
  open,
  onClose,
  onSelect,
  selectedId,
  title,
  isManufacturerOnly = false,
  placeholder
}) {
  const { loginData } = useSelector((state) => state?.main);
  const userId = loginData?.userInformation?.UserId ?? 0;

  const [searchText, setSearchText] = useState("");
  const [list, setList] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [isNewManufacturer, setIsNewManufacturer] = useState(false);
  const [addingCompany, setAddingCompany] = useState(false);

  const debouncedSearch = UseDebounce(searchText, 400);
  const isInitialMount = useRef(true);

  const fetchCompanies = useCallback(
    async (search = "", pageNum = 1, append = false) => {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      try {
        const payload = {
          PageNr: pageNum,
          NrOfRecPerPage: PAGE_SIZE,
          FullSearch: search?.trim() || "",
          UserId: userId,
          IncludeRecordNr: "true",
          TypeOfObjectReturned: "",
          FetchAllowedRecordsOnly: true,
          SearchList: isManufacturerOnly ? [{ manufacturer: true }] : [{}],
          SortList: [
            { FieldName: "Verified", Direction: "DESC" },
            { FieldName: "Name", Direction: "ASC" }
          ]
        };

        const response = await Postrequestcall(
          FETCH_COMPANIES_BY_NAME_NEW,
          payload,
          loginData?.accessToken
        );

        if (response?.data?.data) {
          const items = response.data.data;
          if (pageNum === 1) {
            setList(items);
          } else {
            setList((prev) => Removeduplicates([...prev, ...items], "name"));
          }
          setPage(pageNum);
          setHasMore(items.length >= PAGE_SIZE);
        } else {
          if (pageNum === 1) setList([]);
          setHasMore(false);
        }
      } catch (error) {
        console.error("CompanySelectModal fetch error:", error);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [userId, isManufacturerOnly, loginData?.accessToken]
  );

  // When modal opens, initialize
  useEffect(() => {
    if (open) {
      setSearchText("");
      setList([]);
      setPage(1);
      setHasMore(true);
      setIsNewManufacturer(isManufacturerOnly);
      isInitialMount.current = true;
      fetchCompanies("", 1, false);
    }
  }, [open, fetchCompanies, isManufacturerOnly]);

  // When search changes (debounced)
  useEffect(() => {
    if (!open) return;
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setPage(1);
    setHasMore(true);
    fetchCompanies(debouncedSearch, 1, false);
  }, [debouncedSearch, open, fetchCompanies]);

  const handleScroll = (e) => {
    const target = e.currentTarget;
    if (
      target.scrollHeight - target.scrollTop - target.clientHeight <= 150 &&
      hasMore &&
      !loadingMore &&
      !loading
    ) {
      fetchCompanies(debouncedSearch, page + 1, true);
    }
  };

  const handleItemClick = (item) => {
    if (item?.recordStatusId === 3) {
      alert(`This ${isManufacturerOnly ? "manufacturer" : "company"} is reported`);
      return;
    }
    if (onSelect) {
      onSelect(item);
    }
    if (onClose) {
      onClose();
    }
  };

  const handleAddNewCompany = async () => {
    const trimmedName = searchText.trim();
    if (!trimmedName) return;

    try {
      setAddingCompany(true);
      const isMfr = isManufacturerOnly ? true : isNewManufacturer;
      const saveObject = {
        userId: Number(userId),
        ReturnRecordId: true,
        ReturnRecordError: true,
        SaveList: [
          {
            Id: 0,
            Name: trimmedName,
            ModifiedBy: Number(userId),
            RecordStatusId: 1,
            Manufacturer: isMfr
          }
        ]
      };
      const res = await Postrequestcall(SAVE_COMPANIES, saveObject, loginData?.accessToken);
      if (res?.data?.data && res?.data?.data[0]?.objData?.id) {
        const newRecord = {
          id: res.data.data[0].objData.id,
          name: trimmedName,
          companyName: trimmedName,
          manufacturer: isMfr
        };
        if (onSelect) {
          onSelect(newRecord);
        }
        if (onClose) {
          onClose();
        }
      } else {
        throw new Error("Failed to add company");
      }
    } catch (error) {
      console.error("handleAddNewCompany error:", error);
      alert("Failed to add company. Please try again.");
    } finally {
      setAddingCompany(false);
    }
  };

  const hasExactMatch = list.some(
    (item) => (item?.name || "").trim().toLowerCase() === searchText.trim().toLowerCase()
  );
  const showCreateOption = searchText.trim().length > 1 && !hasExactMatch && !loading;

  const modalTitle =
    title || (isManufacturerOnly ? "Select Manufacturer" : "Select Company");
  const searchPlaceholder =
    placeholder || (isManufacturerOnly ? "Search manufacturers..." : "Search companies...");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      style={{ zIndex: 1500 }}
      sx={{ zIndex: 1500 }}
      slotProps={{
        paper: {
          sx: { borderRadius: 3, overflow: "hidden" }
        }
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
          px: 3,
          pt: 2.5
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {modalTitle}
        </Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: "text.secondary" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        dividers
        onScroll={handleScroll}
        sx={{ minHeight: "360px", maxHeight: "65vh", p: 2.5 }}
      >
        {/* Search Input */}
        <TextField
          fullWidth
          size="small"
          placeholder={searchPlaceholder}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchText ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchText("");
                      setPage(1);
                      setHasMore(true);
                      fetchCompanies("", 1, false);
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null
            }
          }}
          sx={{ mb: 2 }}
        />

        {/* Create new company / manufacturer option */}
        {showCreateOption && (
          <Box
            sx={{
              p: 1.5,
              mb: 2,
              borderRadius: 2,
              border: "1px dashed",
              borderColor: "primary.main",
              backgroundColor: "rgba(8, 61, 184, 0.04)"
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, wordBreak: "break-word" }}>
                  {searchText.trim()}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {isManufacturerOnly || isNewManufacturer
                    ? "Will be created as Manufacturer"
                    : "Will be created as Company"}
                </Typography>
              </Box>
              {!isManufacturerOnly && (
                <ToggleButtonGroup
                  size="small"
                  value={isNewManufacturer ? "manufacturer" : "company"}
                  exclusive
                  onChange={(e, val) => {
                    if (val !== null) {
                      setIsNewManufacturer(val === "manufacturer");
                    }
                  }}
                  sx={{
                    "& .MuiToggleButton-root": {
                      textTransform: "none",
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      px: 1.2,
                      py: 0.35,
                      borderColor: "divider",
                      "&.Mui-selected": {
                        bgcolor: "primary.main",
                        color: "white",
                        "&:hover": { bgcolor: "primary.dark" }
                      }
                    }
                  }}
                >
                  <ToggleButton value="company">Company</ToggleButton>
                  <ToggleButton value="manufacturer">Manufacturer</ToggleButton>
                </ToggleButtonGroup>
              )}
            </Box>
            <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
              <Button
                loading={addingCompany}
                color="primary"
                variant="contained"
                size="small"
                onClick={handleAddNewCompany}
                sx={{ textTransform: "none", fontWeight: 700, px: 2 }}
              >
                + Add
              </Button>
            </Box>
          </Box>
        )}

        {/* List content */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 8 }}>
            <CircularProgress size={36} />
          </Box>
        ) : list.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {isManufacturerOnly ? "No manufacturers found" : "No companies found"}
            </Typography>
            {searchText && !showCreateOption && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                No results matched &ldquo;{searchText}&rdquo;.
              </Typography>
            )}
          </Box>
        ) : (
          <Stack spacing={0.75}>
            {list.map((item, index) => {
              const isSelected = Number(selectedId) === Number(item?.id);
              const isReported = item?.recordStatusId === 3;
              const isManufacturer = !!item?.manufacturer && !isManufacturerOnly;
              const isVerified = Boolean(item?.verified || item?.isVerified || item?.isCompanyVerified);

              return (
                <Box
                  key={item?.id ?? index}
                  onClick={() => handleItemClick(item)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    px: 2,
                    py: 1.5,
                    borderRadius: 2,
                    cursor: isReported ? "not-allowed" : "pointer",
                    opacity: isReported ? 0.6 : 1,
                    border: "1px solid",
                    borderColor: isSelected ? "primary.main" : "divider",
                    bgcolor: isSelected ? "action.selected" : "background.paper",
                    "&:hover": {
                      bgcolor: isReported ? "background.paper" : "action.hover",
                      borderColor: isReported ? "divider" : "primary.light"
                    },
                    transition: "all 0.15s ease"
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      noWrap
                      sx={{
                        fontWeight: isSelected ? 700 : 500,
                        color: isSelected ? "primary.main" : "text.primary"
                      }}
                    >
                      {item?.name}
                    </Typography>
                    {isVerified && (
                      <Tooltip title="This company has been verified" arrow>
                        <VerifiedIcon sx={{ color: "#083DB8", fontSize: "1.1rem" }} />
                      </Tooltip>
                    )}
                  </Box>

                  <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
                    {isReported && (
                      <Tooltip
                        title={`This ${isManufacturerOnly || isManufacturer ? "manufacturer" : "company"} is reported`}
                        arrow
                      >
                        <IconButton
                          size="small"
                          sx={{ color: "error.main" }}
                          onClick={(e) => {
                            e.stopPropagation();
                            alert(`This ${isManufacturerOnly || isManufacturer ? "manufacturer" : "company"} is reported`);
                          }}
                        >
                          <WarningIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {isManufacturer && (
                      <Box
                        component="span"
                        sx={{
                          fontSize: "0.75rem",
                          bgcolor: "error.light",
                          color: "error.contrastText",
                          px: 1,
                          py: 0.25,
                          borderRadius: 1
                        }}
                      >
                        Manufacturer
                      </Box>
                    )}
                  </Stack>
                </Box>
              );
            })}

            {loadingMore && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}